import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronRight, Copy, Loader2, RefreshCw, Unplug } from 'lucide-react';
import { toast } from 'sonner';
import { useConfigurationCopy } from '@/components/WebhookEndpoint';
import { useTimezone } from '@/components/TimezoneContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api/integrations/meta`;

// Paste into each ad's "URL parameters" field in Ads Manager. Ids are the join key; the names are display-only.
export const URL_TEMPLATE = 'utm_source=facebook&utm_medium=paid&utm_campaign={{campaign.name}}&utm_term={{adset.name}}&utm_content={{ad.name}}'
  + '&campaign_id={{campaign.id}}&adset_id={{adset.id}}&ad_id={{ad.id}}&placement={{placement}}&site_source={{site_source_name}}';

const inputStyle = { backgroundColor: '#ffffff', border: '1px solid var(--stroke)', color: 'var(--text)', height: 36 };
const btnPrimary = { height: 36, backgroundColor: 'var(--ink)', color: '#ffffff', border: '1px solid var(--ink)' };
const btnGhost = { height: 36, border: '1px solid var(--stroke)', color: 'var(--text-muted)', backgroundColor: '#fff' };
const MetaMark = () => <span aria-hidden="true" className="inline-grid place-items-center rounded-full text-[11px] font-bold" style={{ width: 16, height: 16, backgroundColor: '#fff', color: '#0866FF' }}>f</span>;

async function call(path, init) {
  const res = await fetch(`${API}${path}`, { headers: { 'Content-Type': 'application/json' }, ...init });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Request failed (${res.status})`);
  return data;
}

/** Settings row: Connect Meta (Facebook Login) → pick ad accounts → sync status; a System User token as the advanced path. */
export function MetaAdsSettings({ Row }) {
  const qc = useQueryClient();
  const { timezone } = useTimezone();
  const [sp, setSp] = useSearchParams();
  const [busy, setBusy] = useState('');
  const pending = useRef(false);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState(null);           // ad account ids being chosen (null = untouched)
  const [advanced, setAdvanced] = useState(false);
  const [purge, setPurge] = useState(false);
  const [token, setToken] = useState('');
  const [accounts, setAccounts] = useState('');
  const { copied, copying, copyError, copy } = useConfigurationCopy(URL_TEMPLATE, 'URL parameters');
  const conn = useQuery({ queryKey: ['meta-connection', timezone], queryFn: () => call(`?tz=${encodeURIComponent(timezone)}`), refetchInterval: d => (d.state.data?.syncing ? 5000 : false) });
  const c = conn.data;

  // Back from Facebook Login: ?meta=connected | ?meta=error&reason=…
  useEffect(() => {
    const m = sp.get('meta');
    if (!m) return;
    if (m === 'connected') toast.success('Meta connected — now pick the ad account(s) to use');
    else toast.error(sp.get('reason') || 'Meta sign-in failed');
    const next = new URLSearchParams(sp); next.delete('meta'); next.delete('reason');
    setSp(next, { replace: true });
    qc.invalidateQueries({ queryKey: ['meta-connection'] });
  }, [sp, setSp, qc]);

  const run = async (which, fn, ok) => {
    if (pending.current) return null;
    pending.current = true;
    setBusy(which);
    setError('');
    try {
      const r = await fn();
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['meta-connection'] }),
        qc.invalidateQueries({ queryKey: ['analytics-dims'] }),
      ]);
      if (ok) toast.success(typeof ok === 'function' ? ok(r) : ok);
      return r;
    } catch (e) {
      const message = e.message || 'Request failed. Please try again.';
      setError(message);
      toast.error(message);
      return null;
    } finally {
      pending.current = false;
      setBusy('');
    }
  };
  const login = () => run('login', async () => { const { url } = await call('/oauth/start'); window.location.assign(url); });
  const saveAccounts = () => run('accounts', () => call('/accounts', { method: 'PUT', body: JSON.stringify({ ad_account_ids: picked }) }), 'Ad accounts saved — syncing').then(result => { if (result) setPicked(null); });
  const refreshAccounts = () => run('refresh', () => call('/accounts?refresh=1'), 'Ad account list refreshed');
  const connectToken = () => {
    if (!token.trim() || !accounts.trim()) return;
    return run('token', () => call('', { method: 'PUT', body: JSON.stringify({ access_token: token, ad_account_ids: accounts.split(/[,\s]+/).filter(Boolean) }) }), 'Meta connected with the token — syncing').then(result => { if (result) { setToken(''); setAdvanced(false); } });
  };
  const sync = () => run('sync', () => call('/sync', { method: 'POST' }), r => r.status === 'already_running' ? 'Sync is already running' : 'Sync started');
  const disconnect = () => {
    if (pending.current) return;
    const message = purge
      ? 'Disconnect Meta and permanently delete all synced campaign names and spend? This cannot be undone.'
      : 'Disconnect Meta? Future syncing will stop. Synced campaign names and spend will be kept.';
    if (!window.confirm(message)) return;
    return run('disconnect', () => call(`?purge=${purge ? 1 : 0}`, { method: 'DELETE' }), 'Meta disconnected').then(result => { if (result) { setPicked(null); setPurge(false); } });
  };

  const chosenIds = picked ?? (c?.ad_accounts || []).map(a => a.id);
  const needsPick = c?.connected && !(c.ad_accounts || []).length;
  const expiresText = c?.token_expires_at ? new Date(c.token_expires_at).toLocaleDateString() : null;

  if (conn.isLoading || conn.isPending) return <Row title="Meta"><p role="status">Loading Meta connection…</p></Row>;
  if (conn.isError) return (
    <Row title="Meta" desc="Connect your ad accounts to sync campaign names and spend.">
      <div role="alert" className="sp-query-error">
        <p>Could not load the Meta connection. Your connection has not been changed.</p>
        <button type="button" disabled={!!busy || conn.isFetching} aria-busy={busy === 'retry'} className="btn" onClick={() => run('retry', () => conn.refetch())}>Try again</button>
      </div>
    </Row>
  );

  return (
    <Row title="Meta" desc="Resolves the campaign, ad set and ad ids on your links to real ads, names and spend. Tether does the tracking; Meta's attribution is never used.">
      <div style={{ maxWidth: 640 }} className="space-y-3">
        {error && <p className="sp-form-error" role="alert">{error}</p>}
        {!c?.connected && (
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={login} disabled={!!busy || c?.oauth_configured === false} className="text-[12px] font-semibold px-3 rounded-md inline-flex items-center gap-2" style={{ ...btnPrimary, backgroundColor: 'var(--ink)' }} data-testid="meta-login">
              {busy === 'login' ? <Loader2 size={12} className="animate-spin" /> : <MetaMark />} Connect Meta
            </button>
            <span className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
              {c?.oauth_configured === false ? 'Facebook Login is not configured on this server yet — use a System User token below.' : 'Signs in with Facebook and asks only for ads_read.'}
            </span>
          </div>
        )}

        {c?.connected && (
          <div className="rounded-md px-3 py-2.5 text-[12px] space-y-1.5" style={{ backgroundColor: 'var(--surface-2)', border: '1px solid var(--stroke-soft)' }}>
            <div className="flex items-center gap-2 flex-wrap">
              <Check size={13} style={{ color: 'var(--mint-success)' }} />
              <strong style={{ color: 'var(--ink)' }}>
                {c.auth_type === 'oauth' ? `Connected as ${c.user_name || 'a Facebook user'}` : 'Connected with a System User token'}
              </strong>
              <span className="ml-auto" style={{ color: 'var(--text-dim)' }}>token {c.token}{expiresText ? ` · valid until ${expiresText}` : ''}</span>
            </div>
            {c.reconnect_soon && <div style={{ color: 'var(--amber-warn)' }}>The Facebook login expires soon — click Reconnect to renew it without losing anything.</div>}
            {c.last_error && <div style={{ color: 'var(--red-error)' }}>
              {c.last_error === 'reconnect_required' ? 'The login is no longer valid — reconnect to continue syncing.'
                : c.last_error === 'no_ad_account_selected' ? 'Pick at least one ad account below.' : c.last_error}
            </div>}

            <div className="pt-1">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold" style={{ color: 'var(--ink)' }}>Ad accounts to use</span>
                {c.auth_type === 'oauth' && <button onClick={refreshAccounts} disabled={!!busy} className="text-[11px] inline-flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                  {busy === 'refresh' ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Refresh list
                </button>}
              </div>
              {(c.available_accounts || []).length ? (
                <div className="rounded-md divide-y" style={{ border: '1px solid var(--stroke-soft)', backgroundColor: '#fff' }}>
                  {c.available_accounts.map(a => {
                    const on = chosenIds.includes(a.id);
                    return (
                      <label key={a.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer">
                        <input type="checkbox" disabled={!!busy} checked={on} onChange={() => setPicked(on ? chosenIds.filter(x => x !== a.id) : [...chosenIds, a.id])} />
                        <span className="flex-1 min-w-0 truncate"><strong style={{ color: 'var(--ink)' }}>{a.name || a.id}</strong> <span style={{ color: 'var(--text-dim)' }}>{a.id}</span></span>
                        <span className="shrink-0" style={{ color: 'var(--text-dim)' }}>{[a.currency, a.timezone_name].filter(Boolean).join(' · ')}</span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-md px-3 py-2" style={{ border: '1px solid var(--stroke-soft)', backgroundColor: '#fff' }}>
                  {(c.ad_accounts || []).map(a => <div key={a.id}><strong style={{ color: 'var(--ink)' }}>{a.name || a.id}</strong> <span style={{ color: 'var(--text-dim)' }}>{a.id} · {[a.currency, a.timezone_name].filter(Boolean).join(' · ')}</span></div>)}
                </div>
              )}
              {(picked !== null || needsPick) && (
                <button onClick={saveAccounts} disabled={!!busy || !chosenIds.length} className="mt-2 text-[12px] font-semibold px-3 rounded-md inline-flex items-center gap-1.5" style={btnPrimary} data-testid="meta-save-accounts">
                  {busy === 'accounts' ? <Loader2 size={12} className="animate-spin" /> : null} {needsPick ? 'Use selected accounts' : 'Save accounts'}
                </button>
              )}
            </div>

            {!needsPick && (
              <div style={{ color: 'var(--text-muted)' }}>
                {c.syncing ? 'Syncing now…' : c.last_sync_at ? `Last sync ${new Date(c.last_sync_at).toLocaleString()}` : 'First sync queued'}
                {c.last_sync_summary && ` · ${Object.values(c.last_sync_summary).reduce((n, s) => n + (s.entities || 0), 0)} ads, ${Object.values(c.last_sync_summary).reduce((n, s) => n + (s.spend_rows || 0), 0)} spend rows`}
                {c.tz_mismatch && <> · ad account timezone differs from your display timezone ({timezone})</>}
              </div>
            )}
            <label className="flex items-center gap-2 pt-2">
              <input type="checkbox" aria-label="Also delete synced campaign names and spend" checked={purge} disabled={!!busy} onChange={e => setPurge(e.target.checked)} />
              Also delete synced campaign names and spend when disconnecting (permanent)
            </label>
            <div className="flex items-center gap-2 pt-1 flex-wrap">
              {c.auth_type === 'oauth' && <button onClick={login} disabled={!!busy} className="text-[12px] font-semibold px-3 rounded-md inline-flex items-center gap-1.5" style={btnGhost}>
                {busy === 'login' ? <Loader2 size={12} className="animate-spin" /> : <MetaMark />} Reconnect
              </button>}
              <button onClick={sync} disabled={!!busy || c.syncing || needsPick} className="text-[12px] font-semibold px-3 rounded-md inline-flex items-center gap-1.5" style={btnGhost}>
                {busy === 'sync' ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Sync now
              </button>
              <button onClick={disconnect} disabled={!!busy} className="text-[12px] px-3 rounded-md inline-flex items-center gap-1.5" style={btnGhost}>
                <Unplug size={12} /> Disconnect
              </button>
            </div>
          </div>
        )}

        <div>
          <button type="button" aria-expanded={advanced} aria-controls="meta-token-form" disabled={!!busy} onClick={() => setAdvanced(v => !v)} className="text-[11px] inline-flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
            {advanced ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Use a System User token instead (agencies, Business Manager)
          </button>
          {advanced && (
            <form id="meta-token-form" aria-label="Connect with a System User token" className="mt-2 space-y-2" onSubmit={e => { e.preventDefault(); connectToken(); }}>
              <div className="sp-settings-inline flex items-center gap-2">
                <input aria-label="Ad account IDs" required disabled={!!busy} value={accounts} onChange={e => setAccounts(e.target.value)} placeholder="Ad account id(s): act_1234567890" className="text-[13px] px-3 rounded-md outline-none" style={{ ...inputStyle, width: 240 }} data-testid="meta-accounts" />
                <input aria-label="System User access token" required disabled={!!busy} value={token} onChange={e => setToken(e.target.value)} type="password" autoComplete="off" placeholder="System User access token (ads_read)"
                  className="flex-1 min-w-0 text-[13px] px-3 rounded-md outline-none" style={inputStyle} data-testid="meta-token" />
                <button type="submit" aria-busy={busy === 'token'} disabled={!!busy || !token.trim() || !accounts.trim()} className="text-[12px] font-semibold px-3 rounded-md inline-flex items-center gap-1.5 shrink-0" style={btnPrimary} data-testid="meta-connect">
                  {busy === 'token' ? <Loader2 size={12} className="animate-spin" /> : null} Connect
                </button>
              </div>
              <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>System User tokens don’t expire; create one in Business Settings → System Users with the ads_read permission on the ad account.</p>
            </form>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[12px] font-semibold" style={{ color: 'var(--ink)' }}>URL parameters for every ad</span>
            <button type="button" aria-label="Copy Meta URL parameters" disabled={copying} aria-busy={copying} onClick={copy} className="text-[11px] inline-flex items-center gap-1 px-2 rounded-md" style={{ ...btnGhost, height: 24 }}>
              {copied ? <Check size={11} /> : <Copy size={11} />} Copy
            </button>
          </div>
          {copyError && <p role="alert" className="sp-form-error">{copyError}</p>}
          <code className="script-snippet block">{URL_TEMPLATE}</code>
          <p className="text-[11px] mt-1 leading-relaxed" style={{ color: 'var(--text-muted)' }}>
            Paste into each ad’s <em>URL parameters</em> field in Ads Manager. Names refresh every 6 hours.
          </p>
        </div>
      </div>
    </Row>
  );
}
